import { mapState, mapGetters } from 'vuex';

/**
 * The sidebar navigation tree and the permission/feature filtering applied to it.
 *
 * Lives in a mixin so the desktop sidebar and the mobile drawer render the same,
 * filtered list. They used to hold two separate copies — the drawer read an
 * unfiltered duplicate out of the `navigation` store — which meant the mobile menu
 * showed every section to everyone regardless of permissions.
 */
export default {
  data() {
    return {
      navigationItems: [
        {
          to: '/dashboard',
          icon: 'fa-home',
          text: 'Dashboard',
          children: [
            {
              to: '/dashboard',
              text: 'My Overview',
              restricted: [],
            },
            {
              to: '/dashboard/my-logged-time',
              text: 'My Logged Time',
              restricted: ['permission:logged-time:read'],
            },
            {
              to: '/dashboard/my-tasks',
              text: 'My Tasks',
              restricted: ['permission:tasks:read'],
            },
            {
              to: '/dashboard/my-forms',
              text: 'My Forms',
              restricted: ['permission:form-submissions:read'],
            },
            {
              to: '/dashboard/upcoming-reviews',
              text: 'My Reviews',
              restricted: ['permission:reviews:read'],
            },
          ],
          restricted: [],
        },
        {
          to: '/contacts',
          icon: 'fa-users',
          text: 'Contacts',
          children: [
            {
              to: '/contacts',
              text: 'Contacts',
              restricted: ['permission:employees:read', 'permission:clients:read', 'permission:external:read'],
            },
            {
              to: '/circle',
              text: 'Circles',
              restricted: ['permission:circles:read'],
            },
            {
              to: '/plans',
              text: 'Plans',
              restricted: ['permission:plans:read'],
            },
          ],
          restricted: [],
        },
        {
          to: '/cases',
          icon: 'fa-briefcase',
          text: 'Cases',
          restricted: ['permission:cases:read'],
        },
        {
          to: '/leads',
          icon: 'fa-user-plus',
          text: 'Leads',
          restricted: ['feature:leads-management', 'permission:leads:read', 'permission:leads:readAll'],
        },
        {
          to: '/referrals',
          icon: 'fa-handshake',
          text: 'Referrals',
          restricted: ['feature:referrals'],
          children: [
            {
              to: '/referrals',
              text: 'My Referrals',
              restricted: ['feature:referrals', 'permission:referrals:read'],
            },
            {
              to: '/referrals/connections',
              text: 'Connections',
              restricted: ['feature:referrals', 'permission:referrals:read'],
            },
          ],
        },
        {
          to: '/business',
          icon: 'fa-building',
          text: 'Business',
          restricted: [],
          children: [
            {
              to: '/business/key-metrics',
              text: 'Key Metrics',
              restricted: ['permission:key-metrics:read'],
            },
            {
              to: '/automations',
              text: 'Automations',
              restricted: ['permission:automations:read', 'permission:automations-blueprints:read'],
            },
            {
              to: '/business/docs',
              text: 'Document Builder',
              restricted: ['permission:document-builder:read', 'permission:document-builder-templates:read'],
            },
            {
              to: '/business/forms',
              text: 'Form Builder',
              restricted: ['permission:forms:read'],
            },
            {
              to: '/business/signatures',
              text: 'Signature Requests',
              restricted: ['permission:signatures:read'],
            },
            {
              to: '/business/income-reconciliation',
              text: 'Income Reconciliation',
              restricted: ['permission:reconciliations:read'],
            },
            {
              to: '/business/reporting',
              text: 'Reporting / Exports',
              restricted: ['permission:reports:read', 'permission:report-templates:read'],
            },
            {
              to: '/business/imports',
              text: 'Imports',
              restricted: ['permission:imports:read'],
            },
          ],
        },
        {
          to: '/settings',
          icon: 'fa-gear',
          text: 'Settings',
          restricted: [],
        },
      ],
    };
  },

  computed: {
    ...mapState('user', ['enabledFeatures']),
    ...mapGetters('user', ['hasEnabledAddon']),

    navigation() {
      return this.navigationItems.map((item) => this.filterAndClone(item)).filter(Boolean);
    },
  },

  methods: {
    hasOpenwork() {
      return this.hasEnabledAddon('openwork');
    },

    hasFeature(feature) {
      if (!feature || typeof feature !== 'string') return false;
      const name = feature.startsWith('feature:') ? feature.slice('feature:'.length) : feature;
      if (name.toLowerCase().includes('openwork')) {
        return this.hasOpenwork() || this.enabledFeatures.includes(name);
      }
      return this.enabledFeatures.includes(name);
    },

    filterAndClone(item) {
      if (item.hasOwnProperty('show') && !item.show) return null;

      const clone = Object.assign({}, item);

      if (Array.isArray(clone.children)) {
        clone.children = clone.children.map((child) => this.filterAndClone(child)).filter(Boolean);

        // A section is only as visible as its children. Hiding the parent behind its own
        // permission list meant one missing permission could take the whole section down
        // (e.g. no clients:read also hid Circles and Plans), so the gates live on the
        // children and the parent follows them. `to` has to follow too — the parent header
        // is itself a link, and left pointing at a child the user can't reach it would
        // bounce them straight back out.
        if (clone.children.length === 0) return null;
        clone.to = clone.children[0].to;
      }

      if (!clone.restricted || !Array.isArray(clone.restricted)) {
        return clone;
      }

      const permissionRestrictions = clone.restricted.filter((r) => r.startsWith('permission:'));
      if (permissionRestrictions.length > 0) {
        const hasAny = permissionRestrictions.some((pr) => this.can(pr.slice('permission:'.length)));
        if (!hasAny) return null;
      }

      const addonRestrictions = clone.restricted.filter((r) => typeof r === 'string' && r.startsWith('addon:'));
      if (addonRestrictions.length > 0) {
        const ok = addonRestrictions.every((ar) => this.hasEnabledAddon(ar.slice('addon:'.length)));
        if (!ok) return null;
      }

      const featureRestrictions = clone.restricted.filter((r) => typeof r === 'string' && r.startsWith('feature:'));
      if (featureRestrictions.length > 0) {
        const ok = featureRestrictions.every((fr) => this.hasFeature(fr));
        if (!ok) return null;
      }

      return clone;
    },
  },
};
